/**
 * Sample cadence helpers for live map history track diagnostics.
 */

/** Fixed history tail window for ongoing track refresh (~2s, matches refresh interval). */
export const LIVE_TRACK_REDIS_TAIL_MS = 2000;

export interface LiveTrackTailQueryWindow {
  queryStartMs: number;
  queryEndMs: number;
}

export interface LiveTrackSampleCadence {
  returnedPoints: number;
  minDeltaMs: number | null;
  maxDeltaMs: number | null;
  approxHz: number | null;
}

export function pointTimestampMs(d: unknown): number {
  if (!d || typeof d !== "object") return 0;
  const point = d as Record<string, unknown>;
  const raw =
    point.timestamp ??
    (point.Datetime instanceof Date
      ? point.Datetime.getTime()
      : point.Datetime != null
        ? new Date(String(point.Datetime)).getTime()
        : 0);
  const ts = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(ts) ? ts : 0;
}

/** Compute the fixed tail history query window for live map track refresh. */
export function computeLiveTrackTailQueryWindow(
  queryEndMs: number,
  tailMs: number = LIVE_TRACK_REDIS_TAIL_MS
): LiveTrackTailQueryWindow {
  const end = Number.isFinite(queryEndMs) ? queryEndMs : Date.now();
  return {
    queryStartMs: Math.max(0, end - tailMs),
    queryEndMs: end,
  };
}

/** Remove duplicate timestamps from a sorted point array (keeps first occurrence). */
export function dedupeTrackPointsByTimestamp<T extends unknown>(points: T[]): T[] {
  if (points.length <= 1) return points;

  const sorted = [...points].sort(
    (a, b) => pointTimestampMs(a) - pointTimestampMs(b)
  );
  const deduped: T[] = [];
  let lastTs = -1;

  for (const point of sorted) {
    const ts = pointTimestampMs(point);
    if (ts <= 0 || ts === lastTs) continue;
    deduped.push(point);
    lastTs = ts;
  }

  return deduped;
}

/** Report timestamp spacing for returned history track points. */
export function computeTrackSampleCadence(points: unknown[]): LiveTrackSampleCadence {
  if (points.length < 2) {
    return {
      returnedPoints: points.length,
      minDeltaMs: null,
      maxDeltaMs: null,
      approxHz: null,
    };
  }

  const timestamps = points
    .map((point) => pointTimestampMs(point))
    .filter((ts) => ts > 0)
    .sort((a, b) => a - b);

  if (timestamps.length < 2) {
    return {
      returnedPoints: points.length,
      minDeltaMs: null,
      maxDeltaMs: null,
      approxHz: null,
    };
  }

  let minDeltaMs = Number.POSITIVE_INFINITY;
  let maxDeltaMs = 0;
  let deltaSum = 0;
  let deltaCount = 0;

  for (let i = 1; i < timestamps.length; i++) {
    const delta = timestamps[i] - timestamps[i - 1];
    if (delta <= 0) continue;
    minDeltaMs = Math.min(minDeltaMs, delta);
    maxDeltaMs = Math.max(maxDeltaMs, delta);
    deltaSum += delta;
    deltaCount++;
  }

  if (deltaCount === 0) {
    return {
      returnedPoints: points.length,
      minDeltaMs: null,
      maxDeltaMs: null,
      approxHz: null,
    };
  }

  const avgDeltaMs = deltaSum / deltaCount;
  const approxHz = avgDeltaMs > 0 ? 1000 / avgDeltaMs : null;

  return {
    returnedPoints: points.length,
    minDeltaMs: Number.isFinite(minDeltaMs) ? minDeltaMs : null,
    maxDeltaMs: maxDeltaMs > 0 ? maxDeltaMs : null,
    approxHz: approxHz != null && Number.isFinite(approxHz) ? approxHz : null,
  };
}
